import { orderBy } from 'lodash-es'
import toast from 'react-hot-toast'

import type { PropsWithClassName, TrackEvent } from '@fv/client-types'
import { getTrackEventDate } from '@fv/models/core'

import { KVDisplay } from '@/components/KVDisplay'
import { AdminButton } from '@/components/shared/AdminButton'
import { AdminDate } from '@/components/shared/AdminDate'
import { AdminLink } from '@/components/shared/AdminLink'
import { AdminSection } from '@/components/shared/AdminSection'
import { AdminTable } from '@/components/shared/AdminTable'
import { AdminTooltip } from '@/components/shared/AdminTooltip'
import { apiUri } from '@/constants'
import type { AdminLoad } from '@/types/admin-load'
import { friendlyDate } from '@/utils'

import { AdminRawResponsePanel } from '../components/AdminRawResponsePanel'
import { useSyncTracking } from '../tracking/mutations'
import { SerializeSlider } from './SerializeSlider'
import type { LoadPanelProps } from './types'

const TrackingHeaderActions = ({ load, onChange }: LoadPanelProps) => {
  const syncTracking = useSyncTracking()
  if (load.isLegacy) return null

  return (
    <>
      <AdminButton
        icon="redo"
        className="btn-sm"
        loading={syncTracking.isPending}
        onClick={() =>
          syncTracking.mutate(load, {
            onSuccess: () => {
              onChange?.()
              toast.success('Tracking synced successfully')
            },
          })
        }
      >
        Trigger tracking update
      </AdminButton>
      {load.carrierTracking && (
        <AdminLink
          icon="square-arrow-up-right"
          href={`${apiUri}/loads/${load._id}/tracking/carrier`}
          rel="noreferrer"
          target="_blank"
        >
          External
        </AdminLink>
      )}
      <V2TrackingControls load={load} />
      <SerializeSlider loadId={load._id} output="tracking" />
    </>
  )
}

export function TrackingPanel({
  load,
  onChange,
  className,
}: PropsWithClassName<LoadPanelProps>) {
  const events = load.track?.trackEvents ?? []

  return (
    <AdminSection
      className={className}
      title="Tracking"
      headerActions={<TrackingHeaderActions {...{ load, onChange }} />}
    >
      <div className="mb-2 xl:grid xl:grid-cols-4">
        <KVDisplay label="Current status">
          {load.track?.currentStatus ?? 'pending'}
        </KVDisplay>
        <KVDisplay label="Tracking number">
          {load.track?.trackingNumber ?? 'N/A'}
        </KVDisplay>
        <KVDisplay label="Last update">
          <AdminDate val={load.track?.lastUpdatedDate} />
        </KVDisplay>
        <KVDisplay label="Actual pickup">
          {friendlyDate(load.track?.pickupDateActual)}
        </KVDisplay>
        <KVDisplay label="Estimated delivery">
          {friendlyDate(load.track?.deliveryDateEstimate)}
        </KVDisplay>

        <KVDisplay label="Actual delivery">
          {friendlyDate(load.track?.deliveryDateActual)}
        </KVDisplay>
      </div>

      <AdminTable
        data={events}
        rowKey={i => JSON.stringify(i)}
        defaultSort="eventDateTime"
        defaultSortDir="desc"
        columns={[
          { key: 'status', label: 'Status', render: d => d.status },
          { key: 'eventType', label: 'Type', render: d => d.eventType },
          { key: 'summary', label: 'Summary', render: d => d.summary },
          {
            key: 'eventSource',
            label: 'Source',
            render: d => `${d.eventSource} (${d.eventMode})`,
          },
          {
            key: 'eventDateTime',
            label: 'Event Date',
            sort: (data, dir) =>
              orderBy(data, d => getTrackEventDate(d.eventDateTime).toDate(), [
                dir,
              ]),
            render: t => <TrackDateTimeComp item={t} />,
          },
        ]}
        collapsible
      />
      {events.some(e => !e.eventDateTime.isUTC) && (
        <div className="px-3 py-1">
          <AdminTooltip
            className="text-sm"
            label={
              <div>
                <p>
                  This carrier has provided us with an ambiguous time (or
                  sometimes no time at all) for their tracking data. Therefore
                  we make no attempts to convert it to any specific timezone.
                </p>
                <p className="mt-4">
                  Shippers will see these times converted to 12H format but no
                  further processing will be done.
                </p>
              </div>
            }
          >
            <AdminLink className="text-xs italic">
              Hover over me for an explanation of timestamps in this data
            </AdminLink>
          </AdminTooltip>
        </div>
      )}
    </AdminSection>
  )
}

type TrackDateTimeProps = {
  item: TrackEvent
}
const TrackDateTimeComp = ({ item }: TrackDateTimeProps) => {
  const date = item.eventDateTime.date
  const time = item.eventDateTime.time
  if (!item.eventDateTime.isUTC) {
    return (
      <span>
        {date} {!!time && ` @ ${time}`}
      </span>
    )
  }
  return <AdminDate val={`${date}T${time}`} />
}

type V2ControlProps = {
  load: AdminLoad
}
const V2TrackingControls = ({ load }: V2ControlProps) => {
  return (
    <AdminRawResponsePanel
      buttonText="V2"
      buttonIcon="bug"
      buttonTooltip="Debug v2 request"
      url={`/loads/${load._id}/tracking/v2`}
    />
  )
}
